import { useState } from 'react'
import { ApiError, admin } from '../api/client'
import type { StationPlan, Vendor } from '../api/types'
import { PROBE_VENDORS, VENDOR_LABEL, externalIdLabel } from '../api/types'

interface Props {
  onCreated: () => void
  onCancel: () => void
}

/** Add a station from a name, a vendor and the vendor's id.
 *
 *  Everything else is derived by asking the vendor: the logger interval from
 *  the gaps between scans, the code from the name, a free schedule slot, and
 *  any channel mappings the rest of the network already agrees on. For MDS
 *  the id is the station_id; for a CMT Edge gateway it is the network id and
 *  every node on the network becomes a channel of the one station.
 *
 *  There is a confirmation step rather than one button, because the probe
 *  proposes channel mappings and a mapping decides where readings land. A
 *  wrong one is not visible until someone reads a chart months later, so it
 *  gets shown before it is written. Everything the probe declined to map is
 *  listed too — those channels wait in the unmapped queue, which is the safe
 *  state, not a failure.
 */
export function AddStation({ onCreated, onCancel }: Props) {
  const [name, setName] = useState('')
  const [vendor, setVendor] = useState<Vendor>('sommer')
  const [externalId, setExternalId] = useState('')
  const [plan, setPlan] = useState<StationPlan>()
  const [interval, setInterval] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  async function probe() {
    setBusy(true)
    setError(undefined)
    try {
      const found = await admin.probeStation(name, externalId, vendor)
      setPlan(found)
      setInterval(found.poll_interval_s ? String(found.poll_interval_s) : '')
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  async function create() {
    if (!plan) return
    setBusy(true)
    setError(undefined)
    try {
      await admin.provisionStation({
        ...plan, poll_interval_s: Number(interval),
      })
      onCreated()
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  if (!plan) {
    return (
      <div className="form">
        <div className="form-grid">
          <label className="field">
            <span>Station name</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="RQ Lajanuri HPP" />
          </label>
          <label className="field">
            <span>Vendor</span>
            <select value={vendor}
                    onChange={(e) => setVendor(e.target.value as Vendor)}>
              {PROBE_VENDORS.map((v) => (
                <option key={v} value={v}>{VENDOR_LABEL[v]}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{vendor === 'cmt_edge' ? 'Gateway network id' : 'MDS station id'}</span>
            <input value={externalId} inputMode="numeric"
                   onChange={(e) => setExternalId(e.target.value)}
                   placeholder={vendor === 'cmt_edge' ? '30413' : '19231638'} />
            <small className="muted">
              {vendor === 'cmt_edge'
                ? 'The network id from the gateway\'s Networks page. Every node on it becomes a channel of this one station.'
                : 'Leading zeros matter: Shaori is 08252104.'}
            </small>
          </label>
        </div>
        <p className="muted">
          The logger interval, the code, the collection time and the channel
          mappings are read from the station itself. Nothing is written until
          you confirm.
        </p>
        {error && <p className="error">{error}</p>}
        <div className="form-actions">
          <button className="button primary" onClick={probe}
                  disabled={busy || !name.trim() || !externalId.trim()}>
            {busy ? `Asking ${VENDOR_LABEL[vendor]}…` : 'Look up station'}
          </button>
          <button className="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="form">
      <h3>{plan.name}</h3>
      <dl className="plan">
        <div><dt>Code</dt><dd><code>{plan.code}</code></dd></div>
        <div><dt>Vendor</dt><dd>{VENDOR_LABEL[plan.vendor]}</dd></div>
        <div><dt>{externalIdLabel(plan.vendor)}</dt><dd><code>{plan.external_id}</code></dd></div>
        <div>
          <dt>Logger interval</dt>
          <dd>
            {plan.poll_interval_s ? (
              <>
                {plan.poll_interval_s / 60} min
                <span className="muted"> measured from {plan.rows_seen} scans</span>
              </>
            ) : (
              <input value={interval} inputMode="numeric" placeholder="seconds"
                     onChange={(e) => setInterval(e.target.value)} />
            )}
          </dd>
        </div>
        <div><dt>Collects daily at</dt><dd>{plan.schedule_time}</dd></div>
        <div><dt>Timezone</dt><dd>{plan.timezone}</dd></div>
        <div>
          <dt>Channels</dt>
          <dd>{plan.mapped} mapped, {plan.unmapped} left for the queue</dd>
        </div>
      </dl>

      <table className="grid">
        <thead>
          <tr><th>Channel</th><th>Reported as</th><th>Will store as</th><th>Why</th></tr>
        </thead>
        <tbody>
          {plan.channels.map((channel) => (
            <tr key={channel.channel_id}>
              <td><code>{channel.channel_id}</code></td>
              <td>
                {channel.source_name}
                {channel.source_unit && channel.source_unit !== '-' && (
                  <span className="muted"> ({channel.source_unit})</span>
                )}
              </td>
              <td>
                {channel.parameter
                  ? <><code>{channel.parameter}</code>
                      {channel.event_driven &&
                        <span className="pill neutral">event-driven</span>}</>
                  : <span className="muted">— queued</span>}
              </td>
              <td className="muted">{channel.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {plan.warnings.length > 0 && (
        <div className="notice notice-warn">
          <strong>Before you confirm</strong>
          <ul>
            {plan.warnings.map((warning) => <li key={warning}>{warning}</li>)}
          </ul>
        </div>
      )}

      {error && <p className="error">{error}</p>}

      <div className="form-actions">
        <button className="button primary" onClick={create}
                disabled={busy || !interval}>
          {busy ? 'Creating…' : `Create ${plan.code}`}
        </button>
        <button className="button" onClick={() => setPlan(undefined)}
                disabled={busy}>
          Back
        </button>
        <button className="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  )
}
